<script setup lang="ts">
import { useStore } from '@/stores/store'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

const store = useStore()
const { cushion, isButtons, edgeSewing, topStitchesType, fabrics, imgPath } = storeToRefs(store)
const optionMappings = {
  cushionType: new Map([
    ['flat_cushion', 1],
    ['box_cushion', 2],
  ]),
  buttons: new Map([
    ['buttons_yes', 1],
    ['buttons_no', 2],
  ]),
  stitches: new Map([
    ['stitches_yes', 1],
    ['stitches_no', 2],
  ]),
  edgeSewing: new Map([
    ['flat_edge', 1],
    ['double_edge', 2],
    ['pipe_edge', 3],
  ]),
  topStitches: new Map([
    ['top_stitches_single', 2],
    ['top_stitches_double', 3],
  ]),
}

const materialMapping = {
  '2': 'Re•Studio Black',
  '3': 'Re•Studio cognac',
  '4': 'Re•Studio Grey',
  '5': 'Re•Studio Ice',
  '6': 'Re•Studio Nougat',
  '7': 'Re•Studio black anilin',
  '8': 'Re•Studio taupe Anilin',
  '9': 'Re•Studio marble anilin',
  '10': 'Re•Studio espresso anilin',
  '11': 'Re•Studio Teak Anilin',
  '12': 'Re•Studio Bronze Anilin',
  '13': 'Re•Studio Nougat Anilin',
  '14': 'Re•Studio Cognac Anilin',
  '15': 'Re•Studio Nougat Anilin',
  '16': 'Re•Studio Sand Anilin',
  '17': 'Re•Studio Savanne Anilin',
  '18': 'Re•Studio LightOlive Anilin',
  '19': 'Re•Studio Peat Anilin',
  '20': 'Re•Studio Reed Anilin',
  '21': 'Re•Studio Sahara Anilin',
  '22': 'Re•Studio Cherry Anilin',
  '23': 'Re•Studio Indigoblue Anilin',
  '24': 'Re•Studio Lavender Anilin',
  '25': 'Re•Studio Fango Anilin',
  '26': 'Re•Studio Mint Anilin',
  '27': 'Re•Studio Porcelain Anilin',
  '28': 'Re•Studio 92 Indigo',
  '29': 'Re•Studio 93 Aqua',
  '30': 'Re•Studio 95 Mineral',
  '31': 'Re•Studio 96 Bright Green',
  '32': 'Re•Studio 98 Glass',
  '33': 'Re•Studio 99 Crystal',
  '34': 'Re•Studio 100 Lagoon',
  '35': 'Re•Studio 102 Pacific',
  '36': 'Re•Studio 103 Sky',
  '37': 'Re•Studio 104 Night',
  '38': 'Re•Studio 106 midnight',
  '39': 'Re•Studio 126 Onyx',
  '40': 'Re•Studio 128 Typhoon',
  '41': 'Re•Studio 129 Savile Grey',
  '42': 'Re•Studio 130 Pelican',
  '43': 'Re•Studio 131 Quartz',
  '44': 'Re•Studio 132 Toned',
  '45': 'Re•Studio 133 Ice White',
  '46': 'Re•Studio 134 Antique Green',
  '47': 'Re•Studio 135 Cypress',
  '48': 'Re•Studio 136 Power Blue',
  '49': 'Re•Studio 137 Wave',
  '50': 'Re•Studio 138 Pool',
  '51': 'Re•Studio 140 Storm',
  '52': 'Re•Studio 141 Cyan',
  '53': 'Re•Studio 142 Gale',
  '54': 'Re•Studio 143 Mint',
  '55': 'Re•Studio 144 Thistle',
  '56': 'Re•Studio 145 Nocturne',
  '57': 'Re•Studio 148 Orchid',
  '58': 'Re•Studio 150 Rosé',
  '59': 'Re•Studio 151 Ruby',
  '60': 'Re•Studio 152 Brick',
  '61': 'Re•Studio 154 Mustard',
  '62': 'Re•Studio 155 Lemongrass',
  '63': 'Re•Studio 156 Onion',
  '64': 'Re•Studio 157 Fudge',
  '65': 'Re•Studio 158 Broken',
  '66': 'Re•Studio 161 Soft Sand',
  '67': 'Nordisktopdesign stof',
}

function getOptionValue(option: string, mapping: Map<string, number>) {
  return mapping.get(option) || 1
}

function getMaterialOptionValue(option: string | never[] | undefined) {
  for (const material in materialMapping) {
    // @ts-expect-error nevermind this typescript behavior
    if (option && option.includes(materialMapping[material])) {
      return material
    }
  }
  return '1'
}

const renderImage = computed(() => {
  const typeOfCushion = getOptionValue(cushion.value, optionMappings.cushionType)
  const buttons = getOptionValue(isButtons.value, optionMappings.buttons)
  const edgeSewingValue = getOptionValue(edgeSewing.value, optionMappings.edgeSewing)
  const topStitches = getOptionValue(topStitchesType.value, optionMappings.topStitches)
  const material = getMaterialOptionValue(fabrics?.value?.data?.name)
  if (import.meta.env.PROD) {
    return `${window.renderpath}${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
  }
  return `${imgPath.value}/renders/${typeOfCushion}_${buttons}_${edgeSewingValue}_${topStitches}_${material}.webp`
})

const backgroundImageUrl = computed(() =>
  renderImage.value
    ? `url("${renderImage.value}")`
    : `url("${imgPath.value}/shapes/cushion_render_bloor.png")`,
)

const finalImageUrl = computed(() =>
  backgroundImageUrl.value.replace(/url\(["']?(.*?)["']?\)/, '$1')
)

watch(finalImageUrl, (url: string) => {
  window.dispatchEvent(new CustomEvent('cushionImageUpdate', {
    detail: { imageUrl: url }
  }))
  window.cushionFinalImageUrl = url
}, { immediate: true })
</script>

<template>
  <div class="loading-wrapper relative" :class="{ render: backgroundImageUrl }">
    <img :src="renderImage" :alt="$t('Cushion type preview')" class="preview-image sticky top-4 bg-gradient-to-bl from-gray-100 to-stone-300 " />
    <div class="bg-gradient-to-b w-full lg:hidden h-1/2 bottom-0 absolute from-transparent to-white"></div>
  </div>
</template>

<style scoped>
.loading-wrapper {
  width: 100%;
}
</style>
